import apiClient, { ApiResponse } from './api';
import { z } from 'zod';

// Validation schemas
const ProfileUpdateSchema = z.object({
  firstName: z.string().min(1).max(50).optional(),
  lastName: z.string().min(1).max(50).optional(),
  name: z.string().min(1).max(100).optional(),
  phone: z.string().optional(),
  location: z.string().max(100).optional(),
  birthday: z.string().max(50).optional(),
  bio: z.string().max(500).optional(),
});

const PasswordChangeSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string()
    .min(8)
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Password must contain uppercase, lowercase, number and special character'
    ),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

// Types
export interface ProfileData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  phone: string;
  location: string;
  birthday: string;
  bio: string;
  profilePic: string;
  role: string;
  accountType: string;
  joinDate: string;
  lastActive: string;
  firstimeSetup: number;
  isActive: boolean;
  credits: {
    total: number;
    used: number;
    available: number;
  };
  connectedServices: {
    shopify: boolean;
    judgeme: boolean;
    yotpo: boolean;
    instagram: boolean;
  };
}

export interface ProfileUpdateData {
  firstName?: string;
  lastName?: string;
  name?: string;
  phone?: string;
  location?: string;
  birthday?: string;
  bio?: string;
}

export interface PasswordChangeData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface SessionData {
  id: string;
  device: string;
  location: string;
  ipAddress: string;
  timestamp: string;
  loginMethod: string;
  success: boolean;
  isCurrent: boolean;
}

/**
 * Profile service for managing user profile data
 */
class ProfileService {
  /**
   * Get user profile data
   */
  async getProfile(): Promise<ProfileData> {
    try {
      console.log('🔍 Fetching user profile...');

      const response = await apiClient.get<{ success: boolean; data: ProfileData }>('/backend/api/profile');

      if (response.data.success) {
        console.log('✅ Profile data fetched successfully');
        return response.data.data;
      } else {
        throw new Error('Failed to fetch profile data');
      }
    } catch (error: any) {
      console.error('❌ Error fetching profile:', error);
      throw new Error(error.message || 'Failed to fetch profile data');
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(profileData: ProfileUpdateData): Promise<ProfileData> {
    try {
      // Validate input
      const validatedData = ProfileUpdateSchema.parse(profileData);

      console.log('📝 Updating user profile...');

      const response = await apiClient.put<{ success: boolean; data: ProfileData; message: string }>(
        '/backend/api/profile',
        validatedData
      );

      if (response.data.success) {
        console.log('✅ Profile updated successfully');
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update profile');
      }
    } catch (error: any) {
      console.error('❌ Error updating profile:', error);

      // Handle validation errors
      if (error instanceof z.ZodError) {
        const firstError = error.errors[0];
        throw new Error(firstError.message);
      }

      throw new Error(error.message || 'Failed to update profile');
    }
  }

  /**
   * Change user password
   */
  async changePassword(passwordData: PasswordChangeData): Promise<void> {
    try {
      // Validate input
      const validatedData = PasswordChangeSchema.parse(passwordData);

      console.log('🔐 Changing user password...');

      const response = await apiClient.post<{ success: boolean; message: string }>(
        '/backend/api/profile/change-password',
        {
          currentPassword: validatedData.currentPassword,
          newPassword: validatedData.newPassword,
        }
      );

      if (response.data.success) {
        console.log('✅ Password changed successfully');
      } else {
        throw new Error(response.data.message || 'Failed to change password');
      }
    } catch (error: any) {
      console.error('❌ Error changing password:', error);

      // Handle validation errors
      if (error instanceof z.ZodError) {
        const firstError = error.errors[0];
        throw new Error(firstError.message);
      }

      // Handle specific error codes
      if (error.code) {
        switch (error.code) {
          case 'INVALID_CURRENT_PASSWORD':
            throw new Error('Current password is incorrect');
          case 'PASSWORD_NOT_SET':
            throw new Error('Password not set. Please use Google OAuth to sign in.');
          default:
            throw new Error(error.message || 'Failed to change password');
        }
      }

      throw new Error(error.message || 'Failed to change password');
    }
  }

  /**
   * Upload user avatar
   */
  async uploadAvatar(file: File): Promise<string> {
    try {
      console.log('📸 Uploading avatar...');

      // Validate file
      if (!file) {
        throw new Error('No file selected');
      }

      if (file.size > 5 * 1024 * 1024) { // 5MB
        throw new Error('File size must be less than 5MB');
      }

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Only image files are allowed (JPEG, PNG, GIF, WebP)');
      }

      // Create form data
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await apiClient.post<{ success: boolean; data: { profilePic: string }; message: string }>(
        '/backend/api/profile/upload-avatar',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        console.log('✅ Avatar uploaded successfully');
        return response.data.data.profilePic;
      } else {
        throw new Error(response.data.message || 'Failed to upload avatar');
      }
    } catch (error: any) {
      console.error('❌ Error uploading avatar:', error);
      throw new Error(error.message || 'Failed to upload avatar');
    }
  }

  /**
   * Get user sessions
   */
  async getSessions(): Promise<{ sessions: SessionData[]; currentSession: any }> {
    try {
      console.log('🔍 Fetching user sessions...');

      const response = await apiClient.get<{ success: boolean; data: { sessions: SessionData[]; currentSession: any } }>(
        '/backend/api/profile/sessions'
      );

      if (response.data.success) {
        console.log('✅ Sessions fetched successfully');
        return response.data.data;
      } else {
        throw new Error('Failed to fetch sessions');
      }
    } catch (error: any) {
      console.error('❌ Error fetching sessions:', error);
      throw new Error(error.message || 'Failed to fetch sessions');
    }
  }

  /**
   * Revoke a session
   */
  async revokeSession(sessionId: string): Promise<void> {
    try {
      console.log('🚫 Revoking session...');

      const response = await apiClient.delete<{ success: boolean; message: string }>(
        `/api/profile/sessions/${sessionId}`
      );

      if (response.data.success) {
        console.log('✅ Session revoked successfully');
      } else {
        throw new Error(response.data.message || 'Failed to revoke session');
      }
    } catch (error: any) {
      console.error('❌ Error revoking session:', error);
      throw new Error(error.message || 'Failed to revoke session');
    }
  }
}

// Create and export singleton instance
const profileService = new ProfileService();
export default profileService;
